import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { AuthService } from 'src/services/auth.service';
import { OAuthService } from 'src/services/oauth.service';
import { AppSessionHealthService } from './app-session-health.service';
import { ConnectivityService } from './connectivity.service';
import { GlobalDataService } from './global-data.service';

@Injectable({
  providedIn: 'root',
})
export class AppResumeService {
  private runInFlight: Promise<void> | null = null;

  constructor(
    private router: Router,
    private oauthService: OAuthService,
    private connectivityService: ConnectivityService,
    private authService: AuthService,
    private gds: GlobalDataService,
    private appSessionHealth: AppSessionHealthService
  ) {}

  run(): Promise<void> {
    if (this.runInFlight) {
      return this.runInFlight;
    }

    this.runInFlight = this.executeRun().finally(() => {
      this.runInFlight = null;
    });

    return this.runInFlight;
  }

  private async executeRun(): Promise<void> {
    if (this.shouldSkip()) {
      return;
    }

    const online = await this.connectivityService.retryCheck();
    if (!online) {
      return;
    }

    if (this.authService.hasStoredSession()) {
      try {
        await this.authService.verifyTokenIntegrity();
      } catch {
        return;
      }
    }

    const agentId =
      this.authService.getAgentIdFromAccessToken() ??
      this.gds.getAgentId().value ??
      null;

    if (agentId != null && String(agentId) !== '') {
      this.appSessionHealth.runAfterAgentAvailable(String(agentId));
    }
  }

  private shouldSkip(): boolean {
    const onLoginRoute = this.router.url.includes('/login');
    const oauthBusy =
      this.oauthService.isLoginBrowserOpen() ||
      this.oauthService.isOAuthExchangeInProgress() ||
      this.oauthService.hasUserDismissedLoginBrowser();

    return onLoginRoute || oauthBusy;
  }
}
